/**
 * Figures placed inside question text.
 *
 * A question (and each part) carries an ordered `figures` list; its text says
 * where each one goes with a marker, `[[fig:f1]]`. A marker alone on its line is
 * a block figure between the lines around it; a marker inside a line is an
 * inline figure the height of the text; a figure no marker places renders after
 * the text, as `questionDiagramUrl` always has.
 *
 * Pure string functions, shared by every renderer and the backend checks, so
 * they agree on what counts as a placed figure.
 *
 * See docs/FeatureDesigns/question-rendering/question-figures-target.md.
 */
import { scanLatex } from './latex/scan';

export interface QuestionFigure {
    /** Unique within the question: 'f1', 'f2'. What a marker names. */
    id: string;
    /** Blob path as stored; the client receives it through `toBlobUrl`. */
    url: string;
    /** SHA256 of the blob — as `questionDiagramHash`. */
    hash?: string;
    /** Short description for screen readers and text-only consumers. */
    alt?: string;
}

export type FigureSegment =
    | { type: 'text'; value: string }
    | { type: 'figure'; id: string; display: 'block' | 'inline' };

const MARKER_RE = /\[\[fig:([A-Za-z0-9_-]+)\]\]/g;

/** Cheap pre-check, so text with no marker skips the scan entirely. */
export function hasFigureMarker(text: string | null | undefined): boolean {
    return !!text && text.includes('[[fig:');
}

/** Markers outside maths, with their offsets. A marker inside `$…$` is part of
 *  the maths — figures inside LaTeX are not supported. */
function markersOf(text: string): { id: string; start: number; end: number }[] {
    const maths = scanLatex(text).filter(s => s.type !== 'text');
    const inMaths = (i: number) => maths.some(s => i >= s.start && i < s.end);
    const found: { id: string; start: number; end: number }[] = [];
    for (const m of text.matchAll(MARKER_RE)) {
        const start = m.index!;
        if (!inMaths(start)) found.push({ id: m[1], start, end: start + m[0].length });
    }
    return found;
}

/**
 * Split text into prose and figure markers. A block marker takes the line
 * breaks either side of it with it, so a block figure doesn't also leave blank
 * lines in pre-wrapped text.
 */
export function splitFigureMarkers(text: string): FigureSegment[] {
    if (!hasFigureMarker(text)) return text ? [{ type: 'text', value: text }] : [];
    const segments: FigureSegment[] = [];
    let cursor = 0;
    for (const m of markersOf(text)) {
        const lineStart = text.lastIndexOf('\n', m.start - 1) + 1;
        const nl = text.indexOf('\n', m.end);
        const lineEnd = nl === -1 ? text.length : nl;
        const block = text.slice(lineStart, m.start).trim() === '' && text.slice(m.end, lineEnd).trim() === '';
        let before = text.slice(cursor, block ? lineStart : m.start);
        if (block) before = before.replace(/\n$/, '');
        if (before) segments.push({ type: 'text', value: before });
        segments.push({ type: 'figure', id: m.id, display: block ? 'block' : 'inline' });
        cursor = block ? (nl === -1 ? text.length : nl + 1) : m.end;
    }
    if (cursor < text.length) segments.push({ type: 'text', value: text.slice(cursor) });
    return segments;
}

/** Ids the text places, in order of first appearance. */
export function figureMarkerIds(text: string | null | undefined): string[] {
    if (!hasFigureMarker(text)) return [];
    return [...new Set(markersOf(text!).map(m => m.id))];
}

/** The figures no marker in `text` places — rendered after the text. */
export function unplacedFigures<F extends { id: string }>(
    text: string | null | undefined,
    figures: readonly F[] | null | undefined,
): F[] {
    if (!figures?.length) return [];
    const placed = new Set(figureMarkerIds(text));
    return figures.filter(f => !placed.has(f.id));
}

/** Markers as a word, for a one-line preview or a plain-text consumer. */
export function figureMarkersAsText(text: string, word = '[figure]'): string {
    if (!hasFigureMarker(text)) return text;
    let out = '';
    let cursor = 0;
    for (const m of markersOf(text)) {
        out += text.slice(cursor, m.start) + word;
        cursor = m.end;
    }
    return out + text.slice(cursor);
}

/**
 * Every figure of a question, stem then parts — for text built from the stem
 * and part statements together (`buildQuestionDisplayText`). Ids are unique
 * within a question, so one list resolves every marker in that text.
 */
export function allQuestionFigures(q: {
    figures?: readonly QuestionFigure[] | null;
    parts?: readonly { figures?: readonly QuestionFigure[] | null }[] | null;
}): QuestionFigure[] {
    return [...(q.figures ?? []), ...(q.parts ?? []).flatMap(p => p.figures ?? [])];
}

/**
 * The text with its figure markers removed: a block marker takes its line with
 * it, an inline one leaves no double space. For a figure that no longer exists,
 * or markers that cannot be trusted to name the right figure. With `ids`, only
 * those figures' markers go and the rest stay where they are.
 */
export function stripFigureMarkers(text: string, ids?: ReadonlySet<string>): string {
    if (!hasFigureMarker(text)) return text;
    let out = '';
    const newline = () => { if (out && !out.endsWith('\n')) out += '\n'; };
    for (const seg of splitFigureMarkers(text)) {
        if (seg.type === 'text') { out += seg.value; continue; }
        const keep = ids !== undefined && !ids.has(seg.id);
        if (seg.display === 'block') {
            newline();
            if (keep) { out += `[[fig:${seg.id}]]`; newline(); }
        } else if (keep) {
            out += `[[fig:${seg.id}]]`;
        }
    }
    return out.replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+([.,;:!?)])/g, '$1').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Markers that name no figure — each text resolves against its own list (the
 * stem against `figures`, a part statement against that part's). A student
 * would see "[figure missing]" for each, so publishing refuses them. A figure no
 * marker places is fine: it renders after the text.
 */
export function danglingFigureMarkers(q: {
    questionText?: string | null;
    figures?: readonly QuestionFigure[] | null;
    parts?: readonly { partId?: string; statement?: string | null; figures?: readonly QuestionFigure[] | null }[] | null;
}): { where: string; id: string }[] {
    const dangling = (where: string, text: string | null | undefined, figures: readonly QuestionFigure[] | null | undefined) => {
        const have = new Set((figures ?? []).map(f => f.id));
        return figureMarkerIds(text).filter(id => !have.has(id)).map(id => ({ where, id }));
    };
    return [
        ...dangling('question', q.questionText, q.figures),
        ...(q.parts ?? []).flatMap((p, i) => dangling(`part ${p.partId ?? i + 1}`, p.statement, p.figures)),
    ];
}
